import type { RetryOptions } from './retryLogic';
import { supabase } from '@/integrations/supabase/client';
import { resolveAdminHubAct } from '@/lib/admin/adminApi';
import { callAction } from '@/lib/rpc/facade';

export type EdgeFunctionResult<T = any> = {
  ok: boolean;
  data?: T;
  error?: any;
};

export type EdgeFunctionOptions = {
  headers?: Record<string, string>;
  retry?: RetryOptions;
};

/**
 * Centralized edge function caller (compat layer)
 * Now routes through AdminApiClient.rpc() to enforce unified client,
 * while preserving the original return shape for existing call sites.
 */
async function callLegacyEdgeFunction<T>(
  functionName: string,
  body?: any,
  options: EdgeFunctionOptions = {}
): Promise<EdgeFunctionResult<T>> {
  const adminAct = resolveAdminHubAct(functionName);

  if (adminAct) {
    try {
      const res = await callAction(adminAct, body, {
        // Preserve original name in logs for easier tracing
        logLabel: `edge-${functionName}`,
      });

      if (res.ok) {
        return { ok: true, data: res.data };
      }

      return { ok: false, error: (res as { ok: false; error: unknown }).error };
    } catch (error) {
      return { ok: false, error };
    }
  }

  try {
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    if (!supabaseUrl) {
      throw new Error('VITE_SUPABASE_URL is not configured');
    }

    const { data: { session } } = await supabase.auth.getSession();

    const headers: Record<string, string> = {
      'content-type': 'application/json',
      ...(options.headers ?? {}),
    };

    if (session?.access_token) {
      headers['authorization'] = `Bearer ${session.access_token}`;
    }

    const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
    if (anonKey) {
      headers['apikey'] = anonKey;
    }

    const payload = body !== undefined ? JSON.stringify(body) : undefined;
    const maxAttempts = options.retry?.maxAttempts ?? 3;
    const baseDelay = options.retry?.baseDelay ?? 200;
    const maxDelay = options.retry?.maxDelay ?? 1000;
    let attempt = 0;

    const executeFetch = async (): Promise<Response> => {
      try {
        // eslint-disable-next-line internal/no-direct-edge-fetch -- Utility function: generic edge function caller with retry logic
        return await fetch(`${supabaseUrl}/functions/v1/${functionName}`, {
          method: 'POST',
          headers,
          body: payload,
        });
      } catch (error) {
        attempt += 1;
        const message = typeof error === 'string'
          ? error
          : (error instanceof Error ? error.message : '');

        if (attempt < maxAttempts && message.includes('NGHTTP2_REFUSED_STREAM')) {
          const delay = Math.min(baseDelay * Math.pow(2, attempt - 1), maxDelay);
          await new Promise((resolve) => setTimeout(resolve, delay));
          return executeFetch();
        }

        throw error;
      }
    };

    const response = await executeFetch();

    const text = await response.text();
    let parsed: unknown = null;
    if (text) {
      try {
        parsed = JSON.parse(text);
      } catch {
        parsed = text;
      }
    }

    if (!response.ok) {
      return {
        ok: false,
        error: parsed ?? { status: response.status, message: response.statusText },
      };
    }

    return { ok: true, data: parsed as T };
  } catch (error) {
    return { ok: false, error };
  }
}

export async function callEdgeFunction<T = any>(
  functionName: string,
  body?: any,
  options?: EdgeFunctionOptions
): Promise<EdgeFunctionResult<T>> {
  // Route all admin function calls through the legacy handler
  // This eliminates the undefined resolveAdminAct reference error
  return callLegacyEdgeFunction<T>(functionName, body, options);
}
